import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ClipboardCheck,
  CalendarPlus,
  Eye,
  History,
  Phone,
  PhoneCall,
  Plus,
  ShieldCheck,
} from 'lucide-react';
import { ACTIONS, RESOURCES, ROLES } from '../config/accessControl';
import {
  CALL_RESULT_LABELS,
  CALL_RESULTS,
  SHEET_STATUS_META,
  SHEET_VALIDATION_STATUSES,
} from '../config/sheetStatus';
import { useAuth } from '../context/AuthContext';
import usePermissions from '../hooks/usePermissions';
import { callControlService } from '../services';
import { sheetService } from '../services';
import Alert from '../components/common/Alert';
import Badge from '../components/common/Badge';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import ConfirmDialog from '../components/common/ConfirmDialog';
import EmptyState from '../components/common/EmptyState';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Modal from '../components/common/Modal';
import Input from '../components/forms/Input';
import Select from '../components/forms/Select';
import Textarea from '../components/forms/Textarea';
import Checkbox from '../components/forms/Checkbox';
import Table from '../components/tables/Table';

const formatDate = (value) =>
  value
    ? new Intl.DateTimeFormat('es-PY', { dateStyle: 'short', timeStyle: 'short' })
      .format(new Date(value))
    : 'Sin llamadas';

function SheetStatus({ status }) {
  const meta = SHEET_STATUS_META[status] || { label: status, variant: 'neutral' };
  return <Badge variant={meta.variant} dot>{meta.label}</Badge>;
}

function CallSheetsPage() {
  const { user, activeCampaign } = useAuth();
  const { can } = usePermissions();
  const [sheets, setSheets] = useState([]);
  const [options, setOptions] = useState({ campaigns: [], coordinators: [], leaders: [] });
  const [filters, setFilters] = useState({
    campaignId: '',
    coordinatorId: '',
    leaderId: '',
    status: '',
    responsePercentage: '',
    date: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [sheetVoters, setSheetVoters] = useState([]);
  const [callSheet, setCallSheet] = useState(null);
  const [callVoterId, setCallVoterId] = useState('');
  const [callResult, setCallResult] = useState(CALL_RESULTS.CALL_BACK);
  const [callNotes, setCallNotes] = useState('');
  const [validationSheet, setValidationSheet] = useState(null);
  const [validationStatus, setValidationStatus] = useState(SHEET_VALIDATION_STATUSES.VALID);
  const [validationObservation, setValidationObservation] = useState('');
  const [validationHistory, setValidationHistory] = useState([]);
  const [validationHistoryLoading, setValidationHistoryLoading] = useState(false);
  const [confirmValidation, setConfirmValidation] = useState(false);
  const [reviewSheet, setReviewSheet] = useState(null);
  const [reviewObservation, setReviewObservation] = useState('');
  const [confirmReview, setConfirmReview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snapshotSheet, setSnapshotSheet] = useState(null);
  const [snapshotHistory, setSnapshotHistory] = useState([]);
  const [snapshotObservation, setSnapshotObservation] = useState('');
  const [snapshotFinal, setSnapshotFinal] = useState(false);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createCampaignId, setCreateCampaignId] = useState('');
  const [createLeaderId, setCreateLeaderId] = useState('');
  const [createObservation, setCreateObservation] = useState('');
  const [createPreview, setCreatePreview] = useState(null);
  const [createPreviewLoading, setCreatePreviewLoading] = useState(false);
  const [viewSheet, setViewSheet] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const canCall = can(RESOURCES.CALL_SHEETS, ACTIONS.CALL) &&
    [ROLES.ADMINISTRADOR, ROLES.ADMINISTRADOR_GENERAL, ROLES.COORDINADOR].includes(user.role);
  const canValidate = can(RESOURCES.CALL_SHEETS, ACTIONS.VALIDATE) &&
    user.role === ROLES.ADMINISTRADOR_GENERAL;
  const canReopen = [ROLES.ADMINISTRADOR, ROLES.ADMINISTRADOR_GENERAL].includes(user.role);
  const canRecordSnapshot = user.role === ROLES.ADMINISTRADOR_GENERAL;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await sheetService.getForActor(user, filters);
      setSheets(response.data);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [filters, user]);

  useEffect(() => {
    sheetService.getOptions(user)
      .then((response) => {
        setOptions(response.data);
        setFilters((current) => ({
          ...current,
          campaignId: current.campaignId || activeCampaign?.id || '',
        }));
      })
      .catch((requestError) => setError(requestError.message));
  }, [activeCampaign?.id, user]);

  useEffect(() => {
    load();
  }, [load]);

  const setFilter = (key, value) => {
    setFilters((current) => {
      const next = { ...current, [key]: value };
      if (key === 'coordinatorId') next.leaderId = '';
      return next;
    });
  };

  const leaders = useMemo(() =>
    options.leaders.filter((item) =>
      !filters.coordinatorId || item.coordinatorId === filters.coordinatorId
    ), [filters.coordinatorId, options.leaders]);

  const openCreate = () => {
    setCreateCampaignId(filters.campaignId || activeCampaign?.id || options.campaigns[0]?.id || '');
    setCreateLeaderId('');
    setCreateObservation('');
    setCreatePreview(null);
    setError('');
    setCreateOpen(true);
  };

  const previewPlanilla = async (leaderId, campaignId = createCampaignId) => {
    setCreateLeaderId(leaderId);
    setCreatePreview(null);
    if (!leaderId) return;
    setCreatePreviewLoading(true);
    setError('');
    try {
      const response = await sheetService.preview(leaderId, campaignId);
      setCreatePreview(response.data);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setCreatePreviewLoading(false);
    }
  };

  const submitCreate = async () => {
    setSaving(true);
    setError('');
    try {
      await sheetService.create({
        // La API asigna la campaña activa. El backend conserva soporte opcional
        // para campaign_id cuando se habilite nuevamente el modo multicampaña.
        leader_id: createLeaderId,
        exception_observation: createObservation,
      });
      setCreateOpen(false);
      setFeedback('La planilla fue creada con todos los votantes activos del puntero y quedó registrada como versión inicial.');
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const totals = useMemo(() => ({
    voters: sheets.reduce((sum, item) => sum + item.total_votantes, 0),
    called: sheets.reduce((sum, item) => sum + item.total_llamados, 0),
    confirmed: sheets.reduce((sum, item) => sum + item.total_confirmados, 0),
  }), [sheets]);

  const loadVoters = async (sheet, onlyWithoutResult = false) => {
    setError('');
    try {
      const response = await sheetService.getVoters(sheet.id, {
        without_call_result: onlyWithoutResult || undefined,
      });
      setSheetVoters(response.data);
      return response.data;
    } catch (requestError) {
      setError(requestError.message);
      return [];
    } finally {
    }
  };

  const openVoters = async (sheet) => {
    setViewSheet(sheet);
    setSheetVoters([]);
    setViewLoading(true);
    await loadVoters(sheet);
    setViewLoading(false);
  };

  const openCall = async (sheet) => {
    setCallSheet(sheet);
    setCallResult(CALL_RESULTS.CALL_BACK);
    setCallNotes('');
    const voters = await loadVoters(sheet, true);
    setCallVoterId(voters[0]?.id || '');
  };

  const submitCall = async () => {
    setSaving(true);
    setError('');
    try {
      await callControlService.createForSheet(callSheet.id, {
        voter_id: callVoterId,
        result: callResult,
        observation: callNotes,
      }, user);
      setCallSheet(null);
      setFeedback('Resultado de llamada registrado. Los totales de la planilla fueron recalculados.');
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const openValidation = async (sheet) => {
    setValidationSheet(sheet);
    setValidationStatus(
      [SHEET_VALIDATION_STATUSES.VALID, SHEET_VALIDATION_STATUSES.INVALID]
        .includes(sheet.validation_status)
        ? sheet.validation_status
        : SHEET_VALIDATION_STATUSES.VALID
    );
    setValidationObservation(sheet.validation_observation || '');
    setValidationHistory([]);
    setError('');
    setValidationHistoryLoading(true);
    try {
      const response = await callControlService.getSheetHistory(sheet.id, user);
      setValidationHistory(response.data);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setValidationHistoryLoading(false);
    }
  };

  const submitValidation = async () => {
    setSaving(true);
    setError('');
    try {
      await sheetService.validate(validationSheet.id, {
        status: validationStatus,
        observation: validationObservation,
      }, user);
      setValidationSheet(null);
      setConfirmValidation(false);
      setFeedback('Estado de validación actualizado y registrado en auditoría.');
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const openReview = (sheet) => {
    setReviewSheet(sheet);
    setReviewObservation('');
    setError('');
  };

  const submitReview = async () => {
    setSaving(true);
    setError('');
    try {
      await sheetService.reopenReview(reviewSheet.id, reviewObservation, user);
      setReviewSheet(null);
      setConfirmReview(false);
      setFeedback('La planilla volvió a revisión y la reapertura quedó auditada.');
      await load();
    } catch (requestError) {
      setError(requestError.message);
      setConfirmReview(false);
    } finally {
      setSaving(false);
    }
  };

  const openSnapshots = async (sheet) => {
    setSnapshotSheet(sheet);
    setSnapshotObservation('');
    setSnapshotFinal(false);
    setSnapshotHistory([]);
    setSnapshotLoading(true);
    setError('');
    try {
      const response = await sheetService.getWeeklySnapshots(sheet.id);
      setSnapshotHistory(response.data);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSnapshotLoading(false);
    }
  };

  const submitSnapshot = async () => {
    setSaving(true);
    setError('');
    try {
      await sheetService.createWeeklySnapshot(snapshotSheet.id, {
        exception_observation: snapshotObservation,
        is_final: snapshotFinal,
      });
      setFeedback('La planilla fue verificada y su composición quedó guardada para comparaciones futuras.');
      const response = await sheetService.getWeeklySnapshots(snapshotSheet.id);
      setSnapshotHistory(response.data);
      setSnapshotObservation('');
      setSnapshotFinal(false);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      key: 'punteroName',
      header: 'Puntero',
      render: (value, item) => (
        <div>
          <button type="button" onClick={() => openVoters(item)} className="font-semibold text-primary-700 hover:underline">{value}</button>
        </div>
      ),
    },
    { key: 'coordinadorName', header: 'Coordinador' },
    { key: 'total_votantes', header: 'Votantes', align: 'right' },
    {
      key: 'version_count',
      header: 'Último control',
      render: (value, item) => item.latest_snapshot
        ? (
          <div>
            <p className="font-medium">V{item.latest_snapshot.version} · {formatDate(item.latest_snapshot.recorded_at)}</p>
            <p className="text-xs text-content-muted">
              {item.latest_snapshot.trend === 'GREW' && `Creció +${item.latest_snapshot.change}`}
              {item.latest_snapshot.trend === 'DECREASED' && `Disminuyó ${item.latest_snapshot.change}`}
              {item.latest_snapshot.trend === 'UNCHANGED' && 'Sin cambios'}
              {item.latest_snapshot.trend === 'BASE' && 'Control inicial'}
            </p>
          </div>
        )
        : 'Sin verificar',
    },
    { key: 'total_llamados', header: 'Llamados', align: 'right' },
    { key: 'total_contactados', header: 'Contestaron', align: 'right' },
    { key: 'total_no_contactados', header: 'No contestaron', align: 'right' },
    { key: 'total_confirmados', header: 'Confirmaron', align: 'right' },
    { key: 'total_rechazados', header: 'Rechazaron', align: 'right' },
    {
      key: 'response_percentage',
      header: 'Respuesta',
      render: (value) => (
        <div className="min-w-24">
          <div className="mb-1 flex justify-between text-xs"><span>{value}%</span></div>
          <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-primary-500" style={{ width: `${value}%` }} />
          </div>
        </div>
      ),
    },
    { key: 'validation_status', header: 'Estado', render: (value) => <SheetStatus status={value} /> },
    { key: 'last_call_at', header: 'Última llamada', render: formatDate },
    {
      key: 'actions',
      header: 'Acciones',
      render: (_, item) => (
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => openVoters(item)}>
            <Eye className="size-4" /> Ver votantes
          </Button>
          <Button variant="ghost" size="sm" onClick={() => openSnapshots(item)}>
            <History className="size-4" /> Verificar
          </Button>
          {canCall && (
            <Button variant="outline" size="sm" onClick={() => openCall(item)} disabled={!item.total_pendientes}>
              <Phone className="size-4" /> Llamar
            </Button>
          )}
          {canValidate && ![SHEET_VALIDATION_STATUSES.VALID, SHEET_VALIDATION_STATUSES.INVALID]
            .includes(item.validation_status) && (
            <Button variant="outline" size="sm" onClick={() => openValidation(item)}>
              <ShieldCheck className="size-4" /> Validar
            </Button>
          )}
          {canReopen && [SHEET_VALIDATION_STATUSES.VALID, SHEET_VALIDATION_STATUSES.INVALID]
            .includes(item.validation_status) && (
            <Button variant="outline" size="sm" onClick={() => openReview(item)}>
              Volver a revisión
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-7 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-content-primary">
            Control de planillas
          </h2>
          <p className="mt-1 text-sm text-content-muted">
            Una planilla se crea tomando todos los votantes activos de un puntero. Cada revisión posterior conserva la composición y sus cambios.
          </p>
        </div>
        <Button onClick={openCreate}><Plus className="size-4" /> Agregar planilla</Button>
      </div>

      {feedback && <Alert variant="success" title="Operación completada" className="mb-5">{feedback}</Alert>}
      {error && !viewSheet && !createOpen && !callSheet && !validationSheet && !reviewSheet && (
        <Alert variant="danger" title="No se pudo completar la solicitud" className="mb-5">{error}</Alert>
      )}

      <div className="mb-5 grid gap-4 sm:grid-cols-3">
        <Card><p className="text-sm text-content-muted">Total de votantes</p><p className="mt-1 text-2xl font-semibold text-content-primary">{totals.voters}</p></Card>
        <Card><p className="text-sm text-content-muted">Votantes llamados</p><p className="mt-1 text-2xl font-semibold text-content-primary">{totals.called}</p></Card>
        <Card><p className="text-sm text-content-muted">Confirmados</p><p className="mt-1 text-2xl font-semibold text-content-primary">{totals.confirmed}</p></Card>
      </div>

      <Card title="Filtros" className="mb-5">
        {/* Selector de campaña reservado para una futura interfaz multicampaña. */}
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <Select id="sheet-coordinator" label="Coordinador" value={filters.coordinatorId} onChange={(event) => setFilter('coordinatorId', event.target.value)} options={options.coordinators.map((item) => ({ value: item.id, label: item.name }))} placeholder="Todos" />
          <Select id="sheet-leader" label="Puntero" value={filters.leaderId} onChange={(event) => setFilter('leaderId', event.target.value)} options={leaders.map((item) => ({ value: item.id, label: item.name }))} placeholder="Todos" />
          <Select id="sheet-status" label="Estado" value={filters.status} onChange={(event) => setFilter('status', event.target.value)} options={Object.entries(SHEET_STATUS_META).map(([value, meta]) => ({ value, label: meta.label }))} placeholder="Todos" />
          <Select id="sheet-response" label="% respuesta mínimo" value={filters.responsePercentage} onChange={(event) => setFilter('responsePercentage', event.target.value)} options={[0, 25, 50, 75, 100].map((value) => ({ value: String(value), label: `${value}% o más` }))} placeholder="Cualquiera" />
          <Input id="sheet-date" type="date" label="Fecha de llamada" value={filters.date} onChange={(event) => setFilter('date', event.target.value)} />
        </div>
      </Card>

      {loading ? (
        <div className="grid min-h-64 place-items-center"><LoadingSpinner label="Cargando planillas" /></div>
      ) : sheets.length ? (
        <Table columns={columns} data={sheets} />
      ) : (
        <EmptyState icon={ClipboardCheck} title="No hay planillas" description="No se encontraron planillas para los filtros seleccionados." />
      )}

      <Modal
        open={Boolean(viewSheet)}
        onClose={() => setViewSheet(null)}
        size="lg"
        panelClassName="max-w-6xl"
        title={viewSheet ? `Votantes de ${viewSheet.punteroName}` : 'Votantes de la planilla'}
        description={viewSheet ? `${viewSheet.total_votantes} votantes activos y estado de sus llamadas` : ''}
        footer={<Button variant="ghost" onClick={() => setViewSheet(null)}>Cerrar</Button>}
      >
        {error && <Alert variant="danger" title="No se pudieron cargar los votantes" className="mb-4">{error}</Alert>}
        {viewLoading ? <LoadingSpinner label="Cargando votantes" /> : (
          <div className="max-h-[60vh] overflow-auto rounded-control border">
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="sticky top-0 bg-slate-50 text-left text-content-muted"><tr><th className="px-4 py-3">Cédula</th><th className="px-4 py-3">Nombre completo</th><th className="px-4 py-3">Teléfono</th><th className="px-4 py-3">Estado de llamada</th><th className="px-4 py-3">Intentos</th><th className="px-4 py-3">Última llamada</th><th className="px-4 py-3">Observación</th><th className="px-4 py-3">Local</th><th className="px-4 py-3">Mesa</th></tr></thead>
              <tbody className="divide-y divide-border">
                {sheetVoters.map((voter) => <tr key={voter.id}>
                  <td className="whitespace-nowrap px-4 py-3">{voter.document}</td>
                  <td className="min-w-44 px-4 py-3 font-medium">{voter.name}</td>
                  <td className="whitespace-nowrap px-4 py-3">{voter.phone || '—'}</td>
                  <td className="whitespace-nowrap px-4 py-3"><Badge variant={voter.lastCall ? (voter.lastCall.result === CALL_RESULTS.CONFIRMED ? 'success' : voter.lastCall.result === CALL_RESULTS.PENDING ? 'warning' : 'info') : 'warning'}>{voter.lastCall ? (CALL_RESULT_LABELS[voter.lastCall.result] || voter.lastCall.result) : 'Sin llamada'}</Badge></td>
                  <td className="px-4 py-3 text-center">{voter.attemptCount || 0}</td>
                  <td className="whitespace-nowrap px-4 py-3">{voter.lastCall ? formatDate(voter.lastCall.called_at) : 'Pendiente'}</td>
                  <td className="min-w-48 px-4 py-3 text-content-muted">{voter.lastCall?.observation || '—'}</td>
                  <td className="min-w-40 px-4 py-3">{voter.voting_location || '—'}</td>
                  <td className="px-4 py-3">{voter.voting_table || '—'}</td>
                </tr>)}
                {!sheetVoters.length && <tr><td colSpan="9" className="px-4 py-8 text-center text-content-muted">No hay votantes activos para mostrar.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      <Modal
        open={createOpen}
        onClose={() => !saving && setCreateOpen(false)}
        size="lg"
        title="Agregar planilla"
        description="Selecciona un puntero para contabilizar todos sus votantes activos."
        footer={<><Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancelar</Button><Button onClick={submitCreate} loading={saving} disabled={!createPreview || Boolean(createPreview.existing_sheet_id) || (createPreview.total_voters < 10 && !createObservation.trim())}><Plus className="size-4" /> Crear planilla</Button></>}
      >
        <div className="space-y-4">
          {error && <Alert variant="danger" title="No se pudo crear la planilla">{error}</Alert>}
          {/* La campaña activa se asigna automáticamente. El selector puede
              restaurarse aquí en una futura versión multicampaña. */}
          <div>
            <Select id="create-sheet-leader" label="Puntero" value={createLeaderId} onChange={(event) => previewPlanilla(event.target.value)} options={options.leaders.map((item) => ({ value: item.id, label: item.name }))} placeholder="Seleccionar puntero" required />
          </div>
          {createPreviewLoading && <LoadingSpinner label="Consultando votantes del puntero" />}
          {createPreview && (
            <>
              {createPreview.existing_sheet_id ? (
                <Alert variant="warning" title="Planilla ya existente">Este puntero ya tiene una planilla para la campaña seleccionada. Puedes abrirla desde el listado y registrar una nueva revisión semanal.</Alert>
              ) : (
                <Alert variant={createPreview.total_voters >= 10 ? 'success' : 'warning'} title={`${createPreview.total_voters} votantes serán contabilizados`}>
                  Se guardarán todos los votantes activos de {createPreview.leader.name}. {createPreview.total_voters < 10 ? 'Al no alcanzar 10, la observación es obligatoria.' : 'Cumple el mínimo de una planilla estándar.'}
                </Alert>
              )}
              {createPreview.total_voters < 10 && !createPreview.existing_sheet_id && <Textarea id="create-sheet-observation" label="Observación de excepción" value={createObservation} onChange={(event) => setCreateObservation(event.target.value)} required hint="Explica por qué se contabiliza la planilla con menos de 10 votantes." />}
              <div className="max-h-72 overflow-y-auto rounded-control border">
                <table className="min-w-full divide-y divide-border text-sm">
                  <thead className="sticky top-0 bg-slate-50 text-left text-content-muted"><tr><th className="px-4 py-3">Cédula</th><th className="px-4 py-3">Nombre completo</th><th className="px-4 py-3">Teléfono</th><th className="px-4 py-3">Contabilización</th></tr></thead>
                  <tbody className="divide-y divide-border">
                    {createPreview.voters.map((voter) => <tr key={voter.id}><td className="px-4 py-3">{voter.document}</td><td className="px-4 py-3 font-medium">{voter.name}</td><td className="px-4 py-3">{voter.phone || 'Sin teléfono'}</td><td className="px-4 py-3"><Badge variant={voter.already_accounted ? 'neutral' : 'info'}>{voter.already_accounted ? 'Ya contabilizado' : 'Se incluirá'}</Badge></td></tr>)}
                    {!createPreview.voters.length && <tr><td colSpan="4" className="px-4 py-8 text-center text-content-muted">El puntero no tiene votantes activos.</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </Modal>

      <Modal open={Boolean(callSheet)} onClose={() => !saving && setCallSheet(null)} size="md" title="Registrar llamada" description={callSheet ? `Planilla de ${callSheet.punteroName}` : ''} footer={<><Button variant="ghost" onClick={() => setCallSheet(null)}>Cancelar</Button><Button onClick={submitCall} loading={saving} disabled={!callVoterId}><PhoneCall className="size-4" /> Guardar llamada</Button></>}>
        <div className="space-y-4">
          {error && <Alert variant="danger" title="No se pudo registrar">{error}</Alert>}
          <Select id="call-voter" label="Votante" value={callVoterId} onChange={(event) => setCallVoterId(event.target.value)} options={sheetVoters.map((item) => ({ value: item.id, label: `${item.name} · ${item.phone || 'Sin teléfono registrado'}` }))} placeholder="Seleccionar votante" required />
          <Select id="call-result" label="Resultado" value={callResult} onChange={(event) => setCallResult(event.target.value)} options={Object.entries(CALL_RESULT_LABELS).map(([value, label]) => ({ value, label }))} />
          <Textarea id="call-notes" label="Observaciones" value={callNotes} onChange={(event) => setCallNotes(event.target.value)} />
        </div>
      </Modal>

      <Modal open={Boolean(validationSheet)} onClose={() => !saving && setValidationSheet(null)} size="lg" title="Validar planilla" description={validationSheet ? validationSheet.punteroName : ''} footer={<><Button variant="ghost" onClick={() => setValidationSheet(null)}>Cancelar</Button><Button onClick={() => setConfirmValidation(true)} disabled={!validationObservation.trim()}><ShieldCheck className="size-4" /> Continuar</Button></>}>
        <div className="space-y-5">
          {error && <Alert variant="danger" title="No se pudo validar">{error}</Alert>}
          {validationSheet && (
            <>
              <div className="grid gap-3 sm:grid-cols-4">
                <Card><p className="text-xs text-content-muted">Votantes</p><p className="mt-1 text-xl font-semibold">{validationSheet.total_votantes}</p></Card>
                <Card><p className="text-xs text-content-muted">Llamados</p><p className="mt-1 text-xl font-semibold">{validationSheet.total_llamados}</p></Card>
                <Card><p className="text-xs text-content-muted">Contestaron</p><p className="mt-1 text-xl font-semibold">{validationSheet.total_contactados}</p></Card>
                <Card><p className="text-xs text-content-muted">Respuesta</p><p className="mt-1 text-xl font-semibold">{validationSheet.response_percentage}%</p></Card>
                <Card><p className="text-xs text-content-muted">Confirmaron</p><p className="mt-1 text-xl font-semibold">{validationSheet.total_confirmados}</p></Card>
                <Card><p className="text-xs text-content-muted">Rechazaron</p><p className="mt-1 text-xl font-semibold">{validationSheet.total_rechazados}</p></Card>
                <Card><p className="text-xs text-content-muted">Números incorrectos</p><p className="mt-1 text-xl font-semibold">{validationSheet.total_numeros_incorrectos}</p></Card>
                <Card><p className="text-xs text-content-muted">Pendientes</p><p className="mt-1 text-xl font-semibold">{validationSheet.total_pendientes}</p></Card>
              </div>
              <Alert
                variant={validationSheet.recommendation.key === 'high' ? 'success' : validationSheet.recommendation.key === 'low' ? 'warning' : 'info'}
                title={`Recomendación: ${validationSheet.recommendation.label}`}
              >
                {validationSheet.recommendation.description} Es informativa y no determina la decisión final.
              </Alert>
              <section>
                <h3 className="mb-2 text-sm font-semibold text-content-primary">Historial de llamadas</h3>
                {validationHistoryLoading ? <LoadingSpinner label="Cargando historial" /> : (
                  <div className="max-h-40 overflow-y-auto rounded-control border">
                    {validationHistory.map((call) => (
                      <div key={call.id} className="flex items-start justify-between gap-3 border-b p-3 text-sm last:border-0">
                        <div><p className="font-medium text-content-primary">{call.voterName} · intento {call.attempt_number}</p><p className="mt-0.5 text-xs text-content-muted">{CALL_RESULT_LABELS[call.result] || call.result}{call.observation ? ` · ${call.observation}` : ''}</p></div>
                        <span className="shrink-0 text-xs text-content-muted">{formatDate(call.called_at)}</span>
                      </div>
                    ))}
                    {!validationHistory.length && <p className="p-4 text-sm text-content-muted">No hay llamadas registradas.</p>}
                  </div>
                )}
              </section>
            </>
          )}
          <Select id="validation-status" label="Estado de la planilla" value={validationStatus} onChange={(event) => setValidationStatus(event.target.value)} options={[{ value: SHEET_VALIDATION_STATUSES.VALID, label: 'Válida' }, { value: SHEET_VALIDATION_STATUSES.INVALID, label: 'Inválida' }]} />
          <Textarea id="validation-observation" label="Observación de validación" required value={validationObservation} onChange={(event) => setValidationObservation(event.target.value)} hint="Obligatoria para cualquier decisión." />
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmValidation}
        onClose={() => !saving && setConfirmValidation(false)}
        onConfirm={submitValidation}
        loading={saving}
        danger={validationStatus === SHEET_VALIDATION_STATUSES.INVALID}
        title={`Confirmar planilla ${validationStatus === SHEET_VALIDATION_STATUSES.VALID ? 'válida' : 'inválida'}`}
        description="La decisión se guardará con tu usuario, fecha, observación y un registro de auditoría."
        confirmLabel="Confirmar validación"
      />

      <Modal open={Boolean(reviewSheet)} onClose={() => !saving && setReviewSheet(null)} size="sm" title="Volver a revisión" description={reviewSheet ? `Acción especial sobre la planilla de ${reviewSheet.punteroName}` : ''} footer={<><Button variant="ghost" onClick={() => setReviewSheet(null)}>Cancelar</Button><Button onClick={() => setConfirmReview(true)} disabled={!reviewObservation.trim()}>Continuar</Button></>}>
        <div className="space-y-4">
          {error && <Alert variant="danger" title="No se pudo reabrir">{error}</Alert>}
          <Alert variant="warning" title="Acción especial">La validación final será retirada y la planilla volverá al estado En proceso.</Alert>
          <Textarea id="review-observation" label="Motivo de reapertura" required value={reviewObservation} onChange={(event) => setReviewObservation(event.target.value)} />
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmReview}
        onClose={() => !saving && setConfirmReview(false)}
        onConfirm={submitReview}
        loading={saving}
        title="Confirmar reapertura"
        description="La planilla volverá a revisión. La validación anterior permanecerá trazable en auditoría."
        confirmLabel="Volver a revisión"
      />

      <Modal
        open={Boolean(snapshotSheet)}
        onClose={() => !saving && setSnapshotSheet(null)}
        size="lg"
        title="Verificación de planilla"
        description={snapshotSheet ? `Planilla de ${snapshotSheet.punteroName}` : ''}
        footer={<Button variant="ghost" onClick={() => setSnapshotSheet(null)}>Cerrar</Button>}
      >
        {snapshotSheet && (
          <div className="space-y-5">
            {error && <Alert variant="danger" title="No se pudo registrar el corte">{error}</Alert>}
            <Alert
              variant={snapshotSheet.total_votantes >= 10 ? 'success' : 'warning'}
              title={snapshotSheet.total_votantes >= 10 ? 'Planilla estándar' : 'Planilla por excepción'}
            >
              Actualmente tiene {snapshotSheet.total_votantes} votantes. Al verificar se guardará exactamente quiénes integran la planilla en este momento. El mínimo de referencia es 10.
              {snapshotSheet.total_votantes < 10 && ' Para contabilizar este control debes explicar la excepción.'}
            </Alert>

            {canRecordSnapshot && !snapshotHistory.some((item) => item.is_final) && (
              <Card title="Verificar planilla esta semana">
                <div className="space-y-4">
                  <Input
                    id="snapshot-recorded-at"
                    label="Fecha y hora del control"
                    value={new Intl.DateTimeFormat('es-PY', {
                      dateStyle: 'full', timeStyle: 'short',
                    }).format(new Date())}
                    readOnly
                    disabled
                    hint="La fecha y hora definitivas serán generadas por el servidor al guardar."
                  />
                  {snapshotSheet.total_votantes < 10 && (
                    <Textarea
                      id="snapshot-exception-observation"
                      label="Observación de excepción"
                      required
                      value={snapshotObservation}
                      onChange={(event) => setSnapshotObservation(event.target.value)}
                      hint="Explica por qué debe contabilizarse con menos de 10 votantes."
                    />
                  )}
                  <Checkbox
                    id="snapshot-final"
                    label="Marcar como planilla final"
                    description="Después de la versión final no se podrán registrar nuevos cortes."
                    checked={snapshotFinal}
                    onChange={(event) => setSnapshotFinal(event.target.checked)}
                  />
                  <Button
                    onClick={submitSnapshot}
                    loading={saving}
                    disabled={snapshotSheet.total_votantes < 10 && !snapshotObservation.trim()}
                  >
                    <CalendarPlus className="size-4" /> Verificar y contabilizar planilla
                  </Button>
                </div>
              </Card>
            )}

            <section>
              <h3 className="mb-3 text-sm font-semibold text-content-primary">Historial de controles</h3>
              {snapshotLoading ? <LoadingSpinner label="Cargando verificaciones" /> : snapshotHistory.length ? (
                <div className="overflow-x-auto rounded-control border">
                  <table className="min-w-full divide-y divide-border text-sm">
                    <thead className="bg-slate-50 text-left text-content-muted"><tr><th className="px-4 py-3">Versión</th><th className="px-4 py-3">Fecha y hora</th><th className="px-4 py-3">Votantes</th><th className="px-4 py-3">Agregados</th><th className="px-4 py-3">Retirados</th><th className="px-4 py-3">Variación</th><th className="px-4 py-3">Resultado</th><th className="px-4 py-3">Condición</th></tr></thead>
                    <tbody className="divide-y divide-border">
                      {snapshotHistory.map((item) => <tr key={item.id}>
                        <td className="px-4 py-3 font-semibold">V{item.version}{item.is_final ? ' · Final' : ''}</td>
                        <td className="whitespace-nowrap px-4 py-3">{formatDate(item.recorded_at)}</td>
                        <td className="px-4 py-3 font-semibold">{item.voter_count}</td>
                        <td className="px-4 py-3">
                          <span className="font-semibold text-success-700">+{item.added_voters.length}</span>
                          {item.added_voters.length > 0 && <details className="mt-1 min-w-40 text-xs text-content-muted"><summary className="cursor-pointer">Ver nombres</summary>{item.added_voters.map((voter) => <p key={voter.id} className="mt-1">{voter.name} · CI {voter.document}</p>)}</details>}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-semibold text-danger-700">-{item.removed_voters.length}</span>
                          {item.removed_voters.length > 0 && <details className="mt-1 min-w-40 text-xs text-content-muted"><summary className="cursor-pointer">Ver nombres</summary>{item.removed_voters.map((voter) => <p key={voter.id} className="mt-1">{voter.name} · CI {voter.document}</p>)}</details>}
                        </td>
                        <td className="px-4 py-3">{item.change === null ? 'Base' : `${item.change >= 0 ? '+' : ''}${item.change}`}</td>
                        <td className="whitespace-nowrap px-4 py-3">{{ BASE: 'Control inicial', GREW: 'Creció', DECREASED: 'Disminuyó', UNCHANGED: 'Sin cambios' }[item.trend] || item.trend}</td>
                        <td className="px-4 py-3"><Badge variant={item.below_minimum ? 'warning' : 'success'}>{item.below_minimum ? 'Excepción' : 'Estándar'}</Badge>{item.exception_observation && <p className="mt-1 max-w-xs text-xs text-content-muted">{item.exception_observation}</p>}</td>
                      </tr>)}
                    </tbody>
                  </table>
                </div>
              ) : <EmptyState icon={History} title="Sin verificaciones" description="Todavía no se verificó esta planilla." />}
            </section>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default CallSheetsPage;
